(() => {
  const ELEMENT_NAME = "header-mobile-navigation";

  if (customElements.get(ELEMENT_NAME)) return;

  class HeaderMobileNavigation extends HTMLElement {
    static TRANSITION_MS = 250;
    static LOCALIZATION_GAP_PX = 4;

    #dialog = null;
    #rootPage = null;
    #activePage = null;
    #lastOpener = null;
    #openButtons = [];
    #backButtons = [];
    #localizationDetails = [];
    /** @type {Map<HTMLDetailsElement, HTMLElement>} */
    #openLocalizationPanels = new Map();
    #transitionTimer = 0;
    #transitionFrame = 0;
    #boundOnOpen = this.#onOpen.bind(this);
    #boundOnBack = this.#onBack.bind(this);
    #boundOnDialogClose = this.#onDialogClose.bind(this);
    #boundOnDialogToggle = this.#onDialogToggle.bind(this);
    #boundOnLocalizationToggle = this.#onLocalizationToggle.bind(this);
    #boundOnRepositionLocalization =
      this.#repositionOpenLocalization.bind(this);

    connectedCallback() {
      this.#dialog = this.closest("dialog");
      this.#rootPage = this.querySelector(
        '[data-mobile-navigation-page="root"]',
      );
      this.#activePage = this.#rootPage;
      this.#openButtons = [
        ...this.querySelectorAll("[data-mobile-navigation-open]"),
      ];
      this.#backButtons = [
        ...this.querySelectorAll("[data-mobile-navigation-back]"),
      ];
      this.#localizationDetails = [
        ...this.querySelectorAll(".footer-localization__disclosure"),
      ];

      for (const button of this.#openButtons) {
        button.addEventListener("click", this.#boundOnOpen);
      }

      for (const button of this.#backButtons) {
        button.addEventListener("click", this.#boundOnBack);
      }

      for (const details of this.#localizationDetails) {
        details.addEventListener("toggle", this.#boundOnLocalizationToggle);
      }

      this.#dialog?.addEventListener("close", this.#boundOnDialogClose);
      this.#dialog?.addEventListener("toggle", this.#boundOnDialogToggle);
      window.addEventListener("resize", this.#boundOnRepositionLocalization);
      this.querySelector(".mobile-navigation__scroll")?.addEventListener(
        "scroll",
        this.#boundOnRepositionLocalization,
        { passive: true },
      );
    }

    disconnectedCallback() {
      for (const button of this.#openButtons) {
        button.removeEventListener("click", this.#boundOnOpen);
      }

      for (const button of this.#backButtons) {
        button.removeEventListener("click", this.#boundOnBack);
      }

      for (const details of this.#localizationDetails) {
        details.removeEventListener("toggle", this.#boundOnLocalizationToggle);
      }

      this.#dialog?.removeEventListener("close", this.#boundOnDialogClose);
      this.#dialog?.removeEventListener("toggle", this.#boundOnDialogToggle);
      window.removeEventListener("resize", this.#boundOnRepositionLocalization);
      this.querySelector(".mobile-navigation__scroll")?.removeEventListener(
        "scroll",
        this.#boundOnRepositionLocalization,
      );
      this.#clearTransitionTimer();
      this.#clearTransitionFrame();
      this.#clearLocalizationPositions();
    }

    #onOpen(event) {
      const button = event.currentTarget;
      const pageId = button.dataset.mobileNavigationOpen;
      const page = this.querySelector(
        `[data-mobile-navigation-page="${CSS.escape(pageId)}"]`,
      );

      if (!page || page === this.#activePage) return;

      this.#lastOpener = button;
      this.#showPage(page, "forward");
    }

    #onBack() {
      if (!this.#rootPage || this.#activePage === this.#rootPage) return;

      this.#showPage(this.#rootPage, "back");
    }

    #showPage(nextPage, direction) {
      const currentPage = this.#activePage;

      if (!currentPage || nextPage === currentPage) return;

      this.#clearTransitionTimer();
      this.#clearTransitionFrame();
      this.#closeLocalizationDetails();
      this.#preparePagesForTransition(currentPage, nextPage);

      if (direction === "forward") {
        this.#resetPageScroll(nextPage);
      }

      nextPage.removeAttribute("hidden");
      this.#activePage = nextPage;
      this.#syncExpandedState();

      if (this.#prefersReducedMotion()) {
        currentPage.classList.remove("mobile-navigation__page--active");
        nextPage.classList.add("mobile-navigation__page--active");
        this.#finalizeOutgoingPage(currentPage);
        this.#focusAfterTransition(nextPage, direction);
        return;
      }

      nextPage.classList.toggle(
        "mobile-navigation__page--from-left",
        direction === "back",
      );
      nextPage.classList.toggle(
        "mobile-navigation__page--from-right",
        direction === "forward",
      );
      // Force layout so the off-screen start transform is committed before paint.
      nextPage.getBoundingClientRect();

      currentPage.classList.toggle(
        "mobile-navigation__page--to-left",
        direction === "forward",
      );
      currentPage.classList.toggle(
        "mobile-navigation__page--to-right",
        direction === "back",
      );
      currentPage.classList.remove("mobile-navigation__page--active");

      // Wait until after paint so the browser can interpolate from the start state.
      this.#transitionFrame = requestAnimationFrame(() => {
        this.#transitionFrame = requestAnimationFrame(() => {
          this.#transitionFrame = 0;

          if (this.#activePage !== nextPage) return;

          nextPage.classList.remove(
            "mobile-navigation__page--from-left",
            "mobile-navigation__page--from-right",
          );
          nextPage.classList.add("mobile-navigation__page--active");

          this.#transitionTimer = window.setTimeout(() => {
            this.#finalizeOutgoingPage(currentPage);
            this.#transitionTimer = 0;
            this.#focusAfterTransition(nextPage, direction);
          }, HeaderMobileNavigation.TRANSITION_MS);
        });
      });
    }

    #syncExpandedState() {
      const activeId =
        this.#activePage && this.#activePage !== this.#rootPage
          ? this.#activePage.dataset.mobileNavigationPage
          : null;

      for (const button of this.#openButtons) {
        const isExpanded = Boolean(
          activeId && button.dataset.mobileNavigationOpen === activeId,
        );
        button.setAttribute("aria-expanded", String(isExpanded));
      }
    }

    #preparePagesForTransition(currentPage, nextPage) {
      for (const page of this.querySelectorAll(
        "[data-mobile-navigation-page]",
      )) {
        this.#clearPageTransitionClasses(page);

        if (page === currentPage || page === nextPage) continue;

        page.setAttribute("hidden", "");
        page.classList.remove("mobile-navigation__page--active");
      }
    }

    #finalizeOutgoingPage(page) {
      if (!page || page === this.#activePage) return;

      page.setAttribute("hidden", "");
      this.#clearPageTransitionClasses(page);
    }

    #clearPageTransitionClasses(page) {
      page?.classList.remove(
        "mobile-navigation__page--from-left",
        "mobile-navigation__page--from-right",
        "mobile-navigation__page--to-left",
        "mobile-navigation__page--to-right",
      );
    }

    #focusAfterTransition(nextPage, direction) {
      if (direction === "forward") {
        nextPage.querySelector("[data-mobile-navigation-back]")?.focus();
        return;
      }

      const opener = this.#lastOpener;

      if (opener?.isConnected && this.#rootPage?.contains(opener)) {
        opener.focus();
        return;
      }

      this.#rootPage?.querySelector("[data-mobile-navigation-open]")?.focus();
    }

    #onDialogClose() {
      this.#reset();
    }

    #onDialogToggle(event) {
      if (event.newState !== "open") return;

      this.#hydrateBagFinderImage();
    }

    #hydrateBagFinderImage() {
      const image = this.querySelector(".mobile-navigation__bag-finder-image");

      if (!(image instanceof HTMLImageElement)) return;
      if (image.dataset.hydrated === "true") return;

      image.dataset.hydrated = "true";
      image.loading = "eager";

      if (image.decode) {
        image.decode().catch(() => {});
      }
    }

    #onLocalizationToggle(event) {
      const details = event.target;

      if (!(details instanceof HTMLDetailsElement)) return;

      if (!details.open) {
        const panel = this.#openLocalizationPanels.get(details);

        if (panel instanceof HTMLElement) {
          this.#clearLocalizationPanelPosition(details, panel);
        }

        return;
      }

      const panel =
        details.querySelector(".footer-localization__panel") ||
        this.#openLocalizationPanels.get(details);

      if (!(panel instanceof HTMLElement)) return;

      this.#positionLocalizationPanel(details, panel);
    }

    #repositionOpenLocalization() {
      for (const [details, panel] of this.#openLocalizationPanels) {
        if (!details.open) continue;

        this.#positionLocalizationPanel(details, panel);
      }
    }

    #positionLocalizationPanel(details, panel) {
      const trigger = details.querySelector("summary");
      const host = this.#dialog;

      if (!(trigger instanceof HTMLElement)) return;
      if (!(host instanceof HTMLElement)) return;

      // Host on the dialog (sibling of the transformed panel) so overflow /
      // transform on the panel cannot clip the dropdown. Menu uses non-modal
      // dialog.show(), so fixed offsets stay viewport-relative.
      if (panel.parentElement !== host) {
        host.append(panel);
      }

      this.#openLocalizationPanels.set(details, panel);

      const hostRect = host.getBoundingClientRect();
      const rect = trigger.getBoundingClientRect();
      const gap = HeaderMobileNavigation.LOCALIZATION_GAP_PX;

      panel.classList.add("is-positioned");
      panel.style.position = "fixed";
      panel.style.left = "auto";
      panel.style.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
      panel.style.width = "max-content";
      panel.style.minWidth = "220px";
      panel.style.zIndex = "4";

      // Measure after fixed so height is accurate, then pick up vs down.
      // Space is relative to the dialog box (overflow:hidden below the header),
      // while fixed offsets stay viewport-relative for non-modal show().
      const panelHeight = panel.getBoundingClientRect().height;
      const spaceAbove = rect.top - hostRect.top - gap;
      const spaceBelow = hostRect.bottom - rect.bottom - gap;
      const openUpward = spaceAbove >= panelHeight || spaceAbove > spaceBelow;

      if (openUpward) {
        panel.style.top = "auto";
        panel.style.bottom = `${Math.max(0, window.innerHeight - rect.top + gap)}px`;
      } else {
        panel.style.bottom = "auto";
        panel.style.top = `${rect.bottom + gap}px`;
      }
    }

    #clearLocalizationPanelPosition(details, panel) {
      if (
        details instanceof HTMLDetailsElement &&
        panel.parentElement === this.#dialog
      ) {
        details.append(panel);
      }

      this.#openLocalizationPanels.delete(details);
      panel.classList.remove("is-positioned");
      panel.style.removeProperty("position");
      panel.style.removeProperty("top");
      panel.style.removeProperty("right");
      panel.style.removeProperty("bottom");
      panel.style.removeProperty("left");
      panel.style.removeProperty("width");
      panel.style.removeProperty("min-width");
      panel.style.removeProperty("z-index");
    }

    #clearLocalizationPositions() {
      for (const [details, panel] of [...this.#openLocalizationPanels]) {
        this.#clearLocalizationPanelPosition(details, panel);
      }
    }

    #closeLocalizationDetails() {
      for (const details of this.#localizationDetails) {
        if (details.open) {
          details.open = false;
        }
      }

      this.#clearLocalizationPositions();
    }

    #resetPageScroll(page) {
      if (!page) return;

      const scrollers = page.querySelectorAll(
        ".mobile-navigation__scroll, .mobile-navigation__child-scroll",
      );

      for (const scroller of scrollers) {
        scroller.scrollTop = 0;
      }
    }

    #reset() {
      this.#clearTransitionTimer();
      this.#clearTransitionFrame();
      this.#closeLocalizationDetails();
      this.#lastOpener = null;

      for (const page of this.querySelectorAll(
        "[data-mobile-navigation-page]",
      )) {
        const isRoot = page === this.#rootPage;
        page.toggleAttribute("hidden", !isRoot);
        page.classList.toggle("mobile-navigation__page--active", isRoot);
        this.#clearPageTransitionClasses(page);
        this.#resetPageScroll(page);
      }

      this.#activePage = this.#rootPage;
      this.#syncExpandedState();
    }

    #prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    #clearTransitionTimer() {
      if (!this.#transitionTimer) return;

      window.clearTimeout(this.#transitionTimer);
      this.#transitionTimer = 0;
    }

    #clearTransitionFrame() {
      if (!this.#transitionFrame) return;

      window.cancelAnimationFrame(this.#transitionFrame);
      this.#transitionFrame = 0;
    }
  }

  customElements.define(ELEMENT_NAME, HeaderMobileNavigation);
})();
